<template>
  <validation-provider
    :name="fieldName || label"
    :rules="computedRules"
    :vid="vid"
    v-slot="validationContext"
    slim
    ref="vProvider"
    :skipIfEmpty="!rules.required" 
  >
    <b-form-group
      v-bind="$attrs"
      :label="fieldLabel"
      :state="getValidationState(validationContext)"
      :label-cols-sm="labelColsSm"
      :label-class="labelClass"
    >
      <template #label>
        {{ fieldLabel }}
        <span v-if="description" v-b-popover.hover.top="description">
          <b-icon-question-circle variant="info" />
        </span>
      </template>

      <slot>
        <b-input-group
          :class="getValidationClass(validationContext)"
          class="date-input-group"
        >
          <b-form-input
            :value="parsedValue"
            type="text"
            :state="getValidationState(validationContext)"
            @beforeinput="checkInputValue"
            @input="handleInputValue"
            :placeholder="placeholder || activeFormatConfig.placeholder"
            :class="`date-input date-input-${_uid}`"
            maxlength="10"
          />

          <b-input-group-append v-if="showPicker">
            <b-datepicker
              :min="min"
              :max="max"
              right
              start-weekday="1"
              button-only
              @input="handlePickerValue"
              :value="parsedPickerValue"
              :locale="$i18n.locale"
            />
          </b-input-group-append>
        </b-input-group>
        <b-form-invalid-feedback>
          {{ validationContext.errors[0] }}
        </b-form-invalid-feedback>
      </slot>
    </b-form-group>
  </validation-provider>
</template>

<script>
import { baseValidatableMixin } from "@/shared";
import { formats } from "@/services/dateTimeService";
import { BIconQuestionCircle } from "bootstrap-vue";

export default {
  components: { BIconQuestionCircle },
  mixins: [baseValidatableMixin],
  data: () => ({
    datePickerFormat: "yyyy-MM-dd",
  }),
  props: {
    min: null,
    max: null,
    dateFormat: {
      type: String,
      default: null,
    },
    rules: {
      type: Object,
      default: () => ({
        required: true,
      }),
    },
    description: {
      type: String,
    },
    showPicker: {
      type: Boolean,
      default: true,
    },
    placeholder: {
      type: String,
      default: null,
    },
  },
  computed: {
    activeFormatConfig() {
      if (formats[this.$i18n.locale]) {
        return formats[this.$i18n.locale].short;
      }

      const lang = this.$i18n.locale.split("-")[0];
      const foundKey = Object.keys(formats).find((k) => k.startsWith(lang));
      if (foundKey) {
        return formats[foundKey].short;
      }

      return formats["nl-NL"].short;
    },
    computedRules() {
      return {
        ...this.rules,
        regex: this.activeFormatConfig.regex,
      };
    },
    parsedValue() {
      return this.shortDateFilter(this.value, this.activeFormatConfig.format);
    },
    parsedPickerValue() {
      if (!this.value) {
        return "";
      }
      let date = this.parseDateIso(this.value);
      return date
        ? this.shortDateFilter(this.value, this.datePickerFormat)
        : "";
    },
  },
  methods: {
    checkInputValue($event) {
      if (!$event.data) return;

      if ($event.inputType && $event.inputType.startsWith("delete")) return;

      if (/^[a-zA-Z]$/.test($event.data)) {
        $event.preventDefault();
        return;
      }

      if ($event.target.value.length >= this.activeFormatConfig.format.length) {
        if ($event.target.selectionStart === $event.target.selectionEnd) {
          $event.preventDefault();
          return;
        }
      }

      if (this.activeFormatConfig.mask) {
        let nextChar = this.activeFormatConfig.mask[$event.target.value.length];
        if (nextChar && nextChar !== "#" && nextChar !== $event.data) {
          $event.preventDefault();
          return;
        }
      }
    },

    handleInputValue(val) {
      if (!val) {
        this.$emit("input", null);
        return;
      }

      if (val.length < this.activeFormatConfig.format.length) {
        if (this.activeFormatConfig.mask) {
          let nextChar;
          while (
            (nextChar = this.activeFormatConfig.mask[val.length]) !== "#"
          ) {
            if (nextChar === undefined) break;
            val = val + nextChar;
            if (val.length >= this.activeFormatConfig.format.length) break;
          }
        }

        if (val.length < this.activeFormatConfig.format.length) {
          return;
        }
      }

      let date = this.parseDateFormat(val, this.activeFormatConfig.format);

      if (date) {
        let utc = this.isoDateToUtc(date);
        let result = this.formatDateToIsoString(utc);
        this.$emit("input", result);
      } else {
        this.$emit("input", null);
      }
    },

    handlePickerValue(val) {
      if (val) {
        let date = this.parseDateIso(val);
        let utc = this.isoDateToUtc(date);
        let result = this.formatDateToIsoString(utc);
        this.$emit("input", result);
      } else {
        this.$emit("input", null);
      }
    },
    shortDateFilter(value, dateFormat) {
      if (!value || value.length < dateFormat.length) {
        return value;
      }
      let date = this.parseDateIso(value);
      return date ? this.formatDate(date, dateFormat) : value;
    },
  },
  mounted() {
    let me = this;
    window.addEventListener("update-date-input-" + this._uid, function(e) {
      me.handleInputValue(e.detail.date);
    });
    window.dispatchEvent(
      new CustomEvent("date-input-loaded", {
        detail: {
          id: this._uid,
        },
      })
    );
  },
};
</script>
